import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AreaChart, Area, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar } from 'recharts';
import { Activity, Wrench, Link2, AlertTriangle, Calendar, Clock, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { t, getLocale } from '@/i18n';

const GlassTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/90 backdrop-blur-md border border-border rounded-lg shadow-lg px-3 py-2">
      <p className="text-[10px] text-muted-foreground">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold" style={{ color: p.color }}>
          {p.value} {p.name === 'output' || p.name === 'actual' ? 't' : ''}
        </p>
      ))}
    </div>
  );
};

export const NodeDetailPanel = ({ node, onClose, onNavigateToOrder, onNavigateToTruck, linkedOrders = [], linkedTrucks = [] }) => {
  if (!node) return null;

  const d = node.data || node;
  const status = d.status;
  const oee = d.oee || { availability: 0, performance: 0, quality: 0, overall: 0 };
  const equipment = d.equipment || {};
  const currentBatch = d.currentBatch || {};
  const alerts = d.alerts || [];
  const history24h = d.history24h || [];
  const historyWeek = d.historyWeek || [];
  const maintenanceHistory = d.maintenanceHistory || [];

  const radarData = [
    { metric: t('node.speed'), value: Math.round(oee.performance) },
    { metric: t('node.quality'), value: Math.round(oee.quality) },
    { metric: t('node.uptime'), value: Math.round(oee.availability) },
    { metric: t('node.energy'), value: Math.round((d.equipment?.hoursRun / (d.equipment?.totalHours || 1)) * 100) || 85 },
    { metric: t('node.output'), value: Math.round((d.capacity?.current / d.capacity?.max) * 100) || 0 },
  ];

  const daysTillMaintenance = equipment.nextMaintenance
    ? Math.max(0, Math.round((new Date(equipment.nextMaintenance) - new Date()) / 86400000))
    : null;

  return (
    <Sheet open={!!node} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-xl w-full bg-white/95 backdrop-blur-xl border-l border-white/40 overflow-y-auto p-0">
        <SheetHeader className={cn(
          'p-6 pb-4 border-b border-border/50',
          status === 'ok' && 'bg-gradient-to-r from-green-50/60 to-transparent',
          status === 'warning' && 'bg-gradient-to-r from-yellow-50/60 to-transparent',
          status === 'error' && 'bg-gradient-to-r from-red-50/60 to-transparent',
        )}>
          <div className="flex items-center gap-3">
            <div className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center',
              'bg-gradient-to-br from-chart-1 to-primary',
            )}>
              {d.icon && <d.icon className="h-5 w-5 text-white" />}
            </div>
            <div>
              <SheetTitle>{d.label}</SheetTitle>
              <SheetDescription className="sr-only">{d.label} node details</SheetDescription>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={cn(
                  'text-xs',
                  status === 'ok' && 'bg-green-100 text-green-700 hover:bg-green-100',
                  status === 'warning' && 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100',
                  status === 'error' && 'bg-red-100 text-red-700 hover:bg-red-100',
                )}>
                  {status === 'ok' ? t('supplyChain.status.operational') : status === 'warning' ? t('supplyChain.status.warning') : t('supplyChain.status.error')}
                </Badge>
                <span className="text-xs text-muted-foreground">OEE {oee.overall}%</span>
              </div>
            </div>
          </div>
        </SheetHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0 px-6">
            <TabsTrigger value="overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none py-3 text-xs">
              <Activity className="h-3.5 w-3.5 mr-1.5" /> {t('node.tab.overview')}
            </TabsTrigger>
            <TabsTrigger value="metrics" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none py-3 text-xs">
              <Package className="h-3.5 w-3.5 mr-1.5" /> {t('node.tab.production')}
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none py-3 text-xs">
              <Wrench className="h-3.5 w-3.5 mr-1.5" /> {t('node.tab.maintenance')}
            </TabsTrigger>
            <TabsTrigger value="connected" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none py-3 text-xs">
              <Link2 className="h-3.5 w-3.5 mr-1.5" /> {t('node.tab.connections')}
            </TabsTrigger>
          </TabsList>

          {/* ─── OVERVIEW TAB ─── */}
          <TabsContent value="overview" className="p-6 space-y-5 mt-0">
            {/* Output vs Capacity */}
            <div>
              <div className="text-xs text-muted-foreground mb-1">{t('node.currentOutput')}</div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-extrabold tracking-tight">{d.capacity?.current || 0}</span>
                <span className="text-sm text-muted-foreground">/ {d.capacity?.max || 0} {d.capacity?.unit || 't/h'}</span>
              </div>
              <Progress value={d.utilization || 0} className="h-2.5 mt-2" />
            </div>

            {/* OEE Breakdown */}
            <div>
              <div className="text-xs font-semibold mb-3">{t('node.oeeBreakdown')}</div>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: t('node.availability'), value: oee.availability, color: 'bg-blue-500' },
                  { label: t('node.performance'), value: oee.performance, color: 'bg-green-500' },
                  { label: t('node.quality'), value: oee.quality, color: 'bg-purple-500' },
                  { label: t('node.overall'), value: oee.overall, color: 'bg-primary' },
                ].map(item => (
                  <div key={item.label} className="text-center">
                    <div className="text-lg font-bold">{item.value}%</div>
                    <Progress value={item.value} className="h-1.5 mt-1" />
                    <div className="text-[10px] text-muted-foreground mt-1">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Current Batch */}
            {currentBatch.material && (
              <Card>
                <CardContent className="p-4">
                  <div className="text-xs font-semibold mb-2">{t('node.currentBatch')}</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground">{t('node.material')}</span>
                      <div className="font-medium">{currentBatch.material}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">{t('node.order')}</span>
                      <div>
                        <Button
                          variant="link"
                          className="p-0 h-auto text-sm font-medium text-primary"
                          onClick={() => onNavigateToOrder?.(currentBatch.orderId)}
                        >
                          {currentBatch.orderId}
                        </Button>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-xs text-muted-foreground">{t('node.progress')}</span>
                      <div className="flex items-center gap-2 mt-1">
                        <Progress value={(currentBatch.producedTons / currentBatch.targetTons) * 100} className="h-2 flex-1" />
                        <span className="text-xs font-semibold">{currentBatch.producedTons}/{currentBatch.targetTons} t</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 24h Output Sparkline */}
            {history24h.length > 0 && (
              <div>
                <div className="text-xs font-semibold mb-2">{t('node.output24h')}</div>
                <ResponsiveContainer width="100%" height={120}>
                  <AreaChart data={history24h}>
                    <defs>
                      <linearGradient id="detailGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                    <XAxis dataKey="hour" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} interval={3} />
                    <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} width={30} />
                    <Tooltip content={<GlassTooltip />} />
                    <Area type="monotone" dataKey="output" stroke="hsl(var(--chart-1))" strokeWidth={2} fill="url(#detailGrad)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </TabsContent>

          {/* ─── METRICS TAB ─── */}
          <TabsContent value="metrics" className="p-6 space-y-5 mt-0">
            {/* Weekly Chart */}
            {historyWeek.length > 0 && (
              <div>
                <div className="text-xs font-semibold mb-2">{t('node.weeklyOutputVsTarget')}</div>
                <ResponsiveContainer width="100%" height={180}>
                  <ComposedChart data={historyWeek}>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={35} />
                    <Tooltip content={<GlassTooltip />} />
                    <Bar dataKey="actual" fill="hsl(var(--chart-1) / 0.75)" radius={[4, 4, 0, 0]} name={t('node.actual')} />
                    <Line type="monotone" dataKey="target" stroke="hsl(23, 81%, 52%)" strokeWidth={2} strokeDasharray="5 5" dot={false} name={t('node.target')} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Key Stats */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: t('node.throughput'), value: `${d.capacity?.current || 0} t/h` },
                { label: t('node.uptime'), value: `${oee.availability}%` },
                { label: t('node.qualityRate'), value: `${oee.quality}%` },
                { label: t('node.utilization'), value: `${d.utilization || 0}%` },
              ].map(stat => (
                <Card key={stat.label}>
                  <CardContent className="p-3 text-center">
                    <div className="text-lg font-bold">{stat.value}</div>
                    <div className="text-[10px] text-muted-foreground">{stat.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Radar Chart */}
            <div>
              <div className="text-xs font-semibold mb-2">{t('node.performanceRadar')}</div>
              <ResponsiveContainer width="100%" height={200}>
                <RadarChart data={radarData} outerRadius="75%">
                  <PolarGrid strokeOpacity={0.3} />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
                  <Radar name="Performance" dataKey="value" stroke="hsl(var(--chart-1))" fill="hsl(var(--chart-1))" fillOpacity={0.2} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          {/* ─── MAINTENANCE TAB ─── */}
          <TabsContent value="maintenance" className="p-6 space-y-5 mt-0">
            {/* Equipment Info */}
            <Card>
              <CardContent className="p-4">
                <div className="text-xs font-semibold mb-3">{t('node.equipment')}</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-xs text-muted-foreground">{t('node.manufacturer')}</span>
                    <div className="font-medium">{equipment.manufacturer || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">{t('node.model')}</span>
                    <div className="font-medium">{equipment.model || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">{t('node.installed')}</span>
                    <div className="font-medium">{equipment.yearInstalled || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">{t('node.operatingHours')}</span>
                    <div className="font-medium">{equipment.hoursRun?.toLocaleString(getLocale()) || 'N/A'} h</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Next Maintenance */}
            {equipment.nextMaintenance && (
              <Card className="border-l-4 border-l-orange-400">
                <CardContent className="p-4 flex items-center gap-3">
                  <Calendar className="h-8 w-8 text-orange-500" />
                  <div>
                    <div className="text-sm font-semibold">{t('node.nextMaintenance')}</div>
                    <div className="text-xs text-muted-foreground">{equipment.nextMaintenance}</div>
                    {daysTillMaintenance !== null && (
                      <Badge variant="outline" className="mt-1 text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        {daysTillMaintenance} {t('node.days')}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Maintenance History */}
            {maintenanceHistory.length > 0 && (
              <div>
                <div className="text-xs font-semibold mb-2">{t('node.maintenanceHistory')}</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">{t('node.date')}</TableHead>
                      <TableHead className="text-xs">{t('node.type')}</TableHead>
                      <TableHead className="text-xs">{t('node.description')}</TableHead>
                      <TableHead className="text-xs text-right">{t('node.duration')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {maintenanceHistory.map((m, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs">{m.date}</TableCell>
                        <TableCell>
                          <Badge variant={m.type === 'Unplanned' ? 'destructive' : 'outline'} className="text-[10px]">
                            {m.type === 'Unplanned' ? t('node.unplanned') : m.type === 'Scheduled' ? t('node.scheduled') : m.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{m.description}</TableCell>
                        <TableCell className="text-xs text-right">{m.duration}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Node Alerts */}
            {alerts.length > 0 && (
              <div>
                <div className="text-xs font-semibold mb-2 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5 text-orange-500" /> {t('node.alerts')}
                </div>
                <div className="space-y-2">
                  {alerts.map(alert => (
                    <Card key={alert.id} className={cn(
                      'border-l-4',
                      alert.severity === 'warning' && 'border-l-yellow-400',
                      alert.severity === 'error' && 'border-l-red-400',
                    )}>
                      <CardContent className="p-3">
                        <div className="text-sm">{t(alert.message)}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {new Date(alert.timestamp).toLocaleString(getLocale(), { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ─── CONNECTED TAB ─── */}
          <TabsContent value="connected" className="p-6 space-y-5 mt-0">
            {/* Linked Orders */}
            <div>
              <div className="text-xs font-semibold mb-2">{t('node.linkedOrders')}</div>
              {linkedOrders.length > 0 ? (
                <div className="space-y-2">
                  {linkedOrders.map(order => (
                    <Card
                      key={order.id}
                      className="cursor-pointer hover:border-primary/30"
                      onClick={() => onNavigateToOrder?.(order.id)}
                    >
                      <CardContent className="p-3 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold">{order.id}</div>
                          <div className="text-xs text-muted-foreground">{order.customer?.name}</div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-[10px]">{order.status}</Badge>
                          <div className="text-xs text-muted-foreground mt-1">
                            {order.items?.reduce((sum, item) => sum + item.quantity, 0)} t
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">{t('node.noLinkedOrders')}</div>
              )}
            </div>

            {/* Linked Trucks */}
            <div>
              <div className="text-xs font-semibold mb-2">{t('node.linkedTrucks')}</div>
              {linkedTrucks.length > 0 ? (
                <div className="space-y-2">
                  {linkedTrucks.map(truck => (
                    <Card
                      key={truck.id}
                      className="cursor-pointer hover:border-primary/30"
                      onClick={() => onNavigateToTruck?.(truck.id)}
                    >
                      <CardContent className="p-3 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-mono font-semibold">{truck.plate}</div>
                          <div className="text-xs text-muted-foreground">{truck.driver?.name} · {truck.company}</div>
                        </div>
                        <Badge className={cn(
                          'text-[10px]',
                          truck.status === 'loading' && 'bg-blue-100 text-blue-700',
                          truck.status === 'waiting' && 'bg-yellow-100 text-yellow-700',
                          truck.status === 'en-route' && 'bg-green-100 text-green-700',
                        )}>
                          {truck.status}
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">{t('node.noLinkedTrucks')}</div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
